import React from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    CircularProgress,
} from "@mui/material";
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    RequestQuote as ECNIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MM/dd/yyyy");
    } catch {
        return dateStr;
    }
}

function linkedLabel(ecn) {
    if (ecn.is_generic) return "Generic";
    if (ecn.project_detail) {
        return `${ecn.project_detail.number} · ${ecn.project_detail.name}`;
    }
    if (ecn.quote_detail) {
        return `${ecn.quote_detail.number} · ${ecn.quote_detail.name}`;
    }
    return "—";
}

export default function ECNsList({ ecns, loading, onView, onEdit, onDelete }) {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (ecns.length === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                <ECNIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">No ECNs found.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {ecns.map((ecn) => (
                <Card
                    key={ecn.id}
                    sx={{
                        boxShadow: "none",
                        border: 1,
                        borderColor: "primary.main",
                    }}
                >
                    <CardActionArea onClick={() => onView(ecn)}>
                        <CardContent
                            sx={{
                                py: 1.5,
                                "&:last-child": { pb: 1.5 },
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                                >
                                    {ecn.number} · {ecn.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {linkedLabel(ecn)}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mt: 0.75,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Chip
                                        label={ecn.is_external ? "External" : "Internal"}
                                        size="small"
                                        color={ecn.is_external ? "secondary" : "default"}
                                    />
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5 }}
                                >
                                    {formatDate(ecn.created)}
                                    {ecn.created_by_detail
                                        ? ` · ${ecn.created_by_detail.full_name || ecn.created_by_detail.username}`
                                        : ""}
                                </Typography>
                            </Box>
                            <ChevronRightIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
                        </CardContent>
                    </CardActionArea>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            px: 1,
                            pb: 1,
                            gap: 0.5,
                        }}
                    >
                        <Tooltip title="View">
                            <IconButton size="small" color="primary" onClick={() => onView(ecn)}>
                                <ViewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onEdit(ecn)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                        <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDelete(ecn)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Card>
            ))}
        </Box>
    );
}
